import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip
} from '@mui/material';
import { Add, Receipt } from '@mui/icons-material';
import { estimatesApi, Estimate } from '../services/estimatesApi';
import { projectsApi, Project } from '../services/projectsApi';
import GenericPage from '../../Commun/components/GenericPage';
import { EstimateGenerateDialog } from '../components/EstimateGenerateDialog';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import './Estimates.css';

const Estimates: React.FC = () => {
    const { t } = useLanguage();
    const [estimates, setEstimates] = useState<Estimate[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        loadEstimates();
        loadProjects();
    }, []);

    const loadEstimates = async () => {
        try {
            setLoading(true);
            const response = await estimatesApi.getAll();
            setEstimates(response.data || []);
        } catch (error) {
            console.error('Error loading estimates:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadProjects = async () => {
        try {
            const response = await projectsApi.getAll();
            setProjects(response.data || []);
        } catch (error) {
            console.error('Error loading projects:', error);
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'draft': return 'default';
            case 'sent': return 'primary';
            case 'approved': return 'success';
            case 'rejected': return 'error';
            default: return 'default';
        }
    };

    return (
        <GenericPage
            title={t('maker.estimates.title')}
            icon={<Receipt />}
            footerEnabled={false}
            additionalActions={
                <Button variant="contained" startIcon={<Add />} onClick={() => setIsModalOpen(true)}>
                    {t('maker.estimates.new')}
                </Button>
            }
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                    {loading ? (
                        <Typography sx={{ textAlign: 'center', mt: 4 }}>{t('maker.estimates.loading')}</Typography>
                    ) : estimates.length === 0 ? (
                        <Paper sx={{ textAlign: 'center', py: 8, borderRadius: 2 }}>
                            <Typography variant="h2" sx={{ mb: 2 }}>💰</Typography>
                            <Typography variant="h6" color="text.secondary">{t('maker.estimates.empty.title')}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{t('maker.estimates.empty.desc')}</Typography>
                            <Button variant="outlined" onClick={() => setIsModalOpen(true)}>{t('maker.estimates.generate')}</Button>
                        </Paper>
                    ) : (
                        <TableContainer component={Paper} sx={{ maxHeight: '100%', boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('maker.estimates.table.number')}</TableCell>
                                        <TableCell>{t('maker.estimates.table.project')}</TableCell>
                                        <TableCell>{t('maker.estimates.table.date')}</TableCell>
                                        <TableCell align="right">{t('maker.estimates.table.subtotal')}</TableCell>
                                        <TableCell align="right">{t('maker.estimates.table.tax')}</TableCell>
                                        <TableCell align="right">{t('maker.estimates.table.total')}</TableCell>
                                        <TableCell>{t('maker.estimates.table.status')}</TableCell>
                                        <TableCell>{t('maker.estimates.table.validity')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {estimates.map((estimate) => (
                                        <TableRow key={estimate.id} hover>
                                            <TableCell>{estimate.estimateNumber}</TableCell>
                                            <TableCell>Project #{estimate.projectId}</TableCell>
                                            <TableCell>{new Date(estimate.estimateDate).toLocaleDateString()}</TableCell>
                                            <TableCell align="right">{formatCurrency(estimate.subTotal)}</TableCell>
                                            <TableCell align="right">{formatCurrency(estimate.taxAmount)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(estimate.totalAmount)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={estimate.status}
                                                    color={getStatusColor(estimate.status) as any}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {estimate.validUntilDate
                                                    ? new Date(estimate.validUntilDate).toLocaleDateString()
                                                    : 'N/A'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>

                <EstimateGenerateDialog
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSuccess={loadEstimates}
                    projects={projects}
                />
            </Box>
        </GenericPage>
    );
};

export default Estimates;

